import type { FluxErrorEntityErrorCodes } from '../../types.js';

export const AutomationV2ExecutionActionGenerateDocumentCodes = {
  AutomationV2ExecutionActionGenerateDocumentDriveFileNotVisible: {
    code: 'automation-v2-execution-action/generate-document-drive-file-not-visible',
    explanation: `O arquivo modelo no Google Drive não tem as permissões corretas.

**Causas comuns:**
- O arquivo está privado
- A conta de serviço da automação não tem acesso ao arquivo

**Como resolver:**
Compartilhe o arquivo do Drive com permissão de leitura pública ou com o e-mail de serviço da integração.`,
    localizations: {},
    message: 'O arquivo do Google Drive não está configurado para acesso público.',
    supportingDocs: [
      {
        name: 'Ação de Gerar Documento',
        path: '/wiki/docs/automations/actions/generate-document',
      },
    ],
  },
  AutomationV2ExecutionActionGenerateDocumentFailedToCreateAttachment: {
    code: 'automation-v2-execution-action/generate-document-failed-to-create-attachment',
    explanation: `O documento foi gerado, mas falhou ao ser anexado ao card.

**Causas comuns:**
- Falha no upload para o storage
- Card foi excluído durante o processo

**Como diagnosticar:**
Tente executar novamente a automação.`,
    localizations: {},
    message: 'Falha ao criar o anexo do documento no card.',
    supportingDocs: [
      {
        name: 'Ação de Gerar Documento',
        path: '/wiki/docs/automations/actions/generate-document',
      },
    ],
  },
  AutomationV2ExecutionActionGenerateDocumentFailedToDownloadTemplate: {
    code: 'automation-v2-execution-action/generate-document-failed-to-download-template',
    explanation: `Não foi possível baixar o arquivo de modelo.

**Causas comuns:**
- Erro de conexão com Google Drive
- Arquivo excluído ou movido

**Como resolver:**
Verifique se o link do template ainda é válido.`,
    localizations: {},
    message: 'Falha ao baixar o template do Google Docs.',
    supportingDocs: [
      {
        name: 'Ação de Gerar Documento',
        path: '/wiki/docs/automations/actions/generate-document',
      },
    ],
  },
  AutomationV2ExecutionActionGenerateDocumentFailedToGetPresignedUrl: {
    code: 'automation-v2-execution-action/generate-document-failed-to-get-presigned-url',
    explanation: `Erro interno ao gerar URL de upload seguro.

**Causas comuns:**
- Instabilidade no serviço de storage

**Como resolver:**
Tente novamente em alguns instantes.`,
    localizations: {},
    message: 'Falha ao obter a URL pré-assinada para o documento.',
    supportingDocs: [
      {
        name: 'Ação de Gerar Documento',
        path: '/wiki/docs/automations/actions/generate-document',
      },
    ],
  },
  AutomationV2ExecutionActionGenerateDocumentFailedToUpload: {
    code: 'automation-v2-execution-action/generate-document-failed-to-upload',
    explanation: `Erro durante o upload do PDF gerado.

**Causas comuns:**
- Arquivo gerado muito grande
- Timeout de conexão

**Como resolver:**
Verifique se o template não gera um arquivo excessivamente grande.`,
    localizations: {},
    message: 'Falha ao fazer o upload do documento.',
    supportingDocs: [
      {
        name: 'Ação de Gerar Documento',
        path: '/wiki/docs/automations/actions/generate-document',
      },
    ],
  },
  AutomationV2ExecutionActionGenerateDocumentInvalidDrivePath: {
    code: 'automation-v2-execution-action/generate-document-invalid-drive-path',
    explanation: `O link fornecido não parece ser de um arquivo Google Drive válido.

**Causas comuns:**
- Link de pasta em vez de arquivo
- Link de outro serviço (Dropbox, OneDrive)
- URL mal formatada

**Como resolver:**
Use o link direto de compartilhamento de um arquivo Google Docs/Slides/Sheets.`,
    localizations: {},
    message: 'A URL fornecida não é um link válido de um arquivo do Google Drive.',
    supportingDocs: [
      {
        name: 'Ação de Gerar Documento',
        path: '/wiki/docs/automations/actions/generate-document',
      },
    ],
  },
  AutomationV2ExecutionActionGenerateDocumentInvalidUrl: {
    code: 'automation-v2-execution-action/generate-document-invalid-url',
    explanation: `A URL do template não tem um formato válido.

**Causas comuns:**
- URL incompleta
- Caracteres inválidos na URL

**Como resolver:**
Copie novamente o link do navegador.`,
    localizations: {},
    message: 'A URL fornecida é inválida.',
    supportingDocs: [
      {
        name: 'Ação de Gerar Documento',
        path: '/wiki/docs/automations/actions/generate-document',
      },
    ],
  },
  AutomationV2ExecutionActionGenerateDocumentMissingCardId: {
    code: 'automation-v2-execution-action/generate-document-missing-card-id',
    explanation: `ID do card é obrigatório no contexto da ação. O id do card é automaticamente passado pelo gatilho ou por ações anteriores.

**Causas comuns:**
- Variável cardId não resolvida
- Caso o valor do cardId seja mapeado manualmente, pode ter sido digitado incorretamente.
- Caso o valor do cardId não tenha sido mapeado, o \`context.card.id\` não está disponível.

**Como resolver:**
Corrija o mapeamento do ID do card.`,
    localizations: {},
    message:
      'Não foi possível gerar o documento pois o ID do card está faltando. Verifique se o card existe no contexto da automação ou se foi digitado corretamente.',
    supportingDocs: [
      {
        name: 'Ação de Gerar Documento',
        path: '/wiki/docs/automations/actions/generate-document',
      },
      {
        name: 'Informações Dinâmicas',
        path: '/wiki/docs/automations/informacoes-dinamicas',
      },
    ],
  },
  AutomationV2ExecutionActionGenerateDocumentUrlNotDrive: {
    code: 'automation-v2-execution-action/generate-document-url-not-drive',
    explanation: `O domínio da URL não pertence ao Google Drive.

**Causas comuns:**
- Uso de encurtadores de URL
- Links de visualizadores externos

**Como resolver:**
Utilize URLs oficias do Google Drive (docs.google.com, drive.google.com).`,
    localizations: {},
    message: 'A URL fornecida não é um link do Google Drive.',
    supportingDocs: [
      {
        name: 'Ação de Gerar Documento',
        path: '/wiki/docs/automations/actions/generate-document',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-execution-action'>;
